import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, Modal, Pagination, Select, Space, Spin, Tabs, Table, Tooltip, message } from 'antd';
import {
  BulbOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  EyeOutlined,
  FileSearchOutlined,
  ReloadOutlined,
  RedoOutlined,
  SearchOutlined,
  StarFilled,
  StopOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  ideaIntelApi,
  type IdeaBatch,
  type IdeaCard,
  type IdeaOverview,
  type IdeaTraceMsg,
} from '../../services/ideaIntelApi';
import './idea-center.css';

const fmtTime = (v?: string | null, format = 'MM-DD HH:mm') => (v ? dayjs(v).format(format) : '-');

const CATEGORIES = ['工具创意', '技术动向', '实践案例', '生态思路'];
const CATEGORY_CLS: Record<string, string> = {
  工具创意: 'ic-cat ic-cat-tool',
  技术动向: 'ic-cat ic-cat-trend',
  实践案例: 'ic-cat ic-cat-case',
  生态思路: 'ic-cat ic-cat-eco',
};
const STATUS_TEXT: Record<string, string> = { new: '新', read: '已读', adopted: '已采纳', ignored: '已忽略' };

const PIPELINE_HINT = (
  <>
    数据链路:微信采集器(开启<b>AI创意情报</b>通道)上传 → 攒批(约25条或30分钟)→ <b>AI 提炼创意卡片</b> → 沉淀到这里。
    <br />
    保持采集器登录并监控,群里聊到 AI 新技术/新工具/新想法后稍等片刻即可看到卡片。
  </>
);

function scoreStars(score: number) {
  return (
    <span className="ic-stars" title={`对智坊可借鉴度 ${score}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <StarFilled key={i} className={i <= score ? 'ic-star-on' : 'ic-star-off'} />
      ))}
    </span>
  );
}

// ===== 溯源弹窗:该创意依据的原始聊天消息 =====
function TraceModal({ idea, onClose }: { idea: IdeaCard | null; onClose: () => void }) {
  const [msgs, setMsgs] = useState<IdeaTraceMsg[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!idea) { setMsgs([]); return; }
    setLoading(true);
    ideaIntelApi
      .ideaTrace(idea.id)
      .then((r) => {
        if (r?.ok) setMsgs(r.messages || []);
        else message.error(r?.reason || '溯源加载失败');
      })
      .catch(() => message.error('溯源加载失败'))
      .finally(() => setLoading(false));
  }, [idea]);

  const convs = useMemo(() => {
    const m = new Map<string, { talkerName: string; isGroup: boolean; msgs: IdeaTraceMsg[] }>();
    for (const msg of msgs) {
      if (!m.has(msg.talker)) m.set(msg.talker, { talkerName: msg.talkerName, isGroup: msg.isGroup, msgs: [] });
      m.get(msg.talker)!.msgs.push(msg);
    }
    return Array.from(m.values());
  }, [msgs]);

  return (
    <Modal
      open={!!idea}
      onCancel={onClose}
      footer={null}
      width={680}
      className="df-readable-modal"
      title={<span><FileSearchOutlined style={{ marginRight: 8, color: '#fbbf24' }} />创意溯源 · 原始聊天</span>}
    >
      {idea && (
        <Spin spinning={loading}>
          <div className="ic-trace-title">{idea.title}</div>
          {convs.length === 0 && !loading && <Empty description="该卡片没有记录源消息" />}
          {convs.map((c, i) => (
            <div key={i} className="ic-trace-conv">
              <div className="ic-trace-conv-head">{c.isGroup ? '👥' : '👤'} {c.talkerName}</div>
              {c.msgs.map((m) => (
                <div key={m.msgId} className="ic-trace-msg">
                  <span className="ic-trace-meta">[{fmtTime(m.time)}] {m.sender || '?'}:</span>
                  <span className="ic-trace-content">{m.content}</span>
                </div>
              ))}
            </div>
          ))}
        </Spin>
      )}
    </Modal>
  );
}

export default function IdeaCenterPage() {
  const [overview, setOverview] = useState<IdeaOverview | null>(null);
  const [ideas, setIdeas] = useState<IdeaCard[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [category, setCategory] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [keyword, setKeyword] = useState('');   // 搜索框输入值
  const [kwQuery, setKwQuery] = useState('');    // 防抖后真正用于查询的关键字
  const [loading, setLoading] = useState(false);
  const [traceIdea, setTraceIdea] = useState<IdeaCard | null>(null);
  const [detailIdea, setDetailIdea] = useState<IdeaCard | null>(null);
  const [batches, setBatches] = useState<IdeaBatch[]>([]);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchPage, setBatchPage] = useState(1);

  const loadIdeas = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const [ov, list] = await Promise.all([
        ideaIntelApi.overview(),
        ideaIntelApi.ideas(p, pageSize, category, status, kwQuery || undefined),
      ]);
      setOverview(ov);
      setIdeas(list.items || []);
      setTotal(list.total || 0);
    } catch {
      message.error('创意列表加载失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, category, status, kwQuery]);

  const loadBatches = useCallback(async (p = batchPage) => {
    try {
      const r = await ideaIntelApi.batches(p, 20);
      setBatches(r.items || []);
      setBatchTotal(r.total || 0);
    } catch {
      message.error('批次日志加载失败');
    }
  }, [batchPage]);

  // 搜索防抖：停止输入 350ms 后才真正查询，并回到第 1 页；避免每敲一下就打一次后台。
  useEffect(() => {
    const t = setTimeout(() => { setKwQuery(keyword.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [keyword]);

  useEffect(() => { loadIdeas(page); }, [page, category, status, kwQuery]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadBatches(batchPage); }, [batchPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const setIdeaStatus = async (idea: IdeaCard, s: string, tip: string) => {
    try {
      const r = await ideaIntelApi.updateStatus(idea.id, s);
      if (r?.ok) {
        message.success(tip);
        setIdeas((prev) => prev.map((it) => (it.id === idea.id ? { ...it, status: s as IdeaCard['status'] } : it)));
        ideaIntelApi.overview().then(setOverview).catch(() => {});
      } else {
        message.error(r?.reason || '操作失败');
      }
    } catch {
      message.error('操作失败');
    }
  };

  /** 采纳:这些创意是"智坊平台本身"的改进灵感,智坊没法自己改自己——
   *  所以采纳只做两件事:标记已采纳 + 把整理好的改进需求复制到剪贴板,
   *  用户粘贴给负责维护智坊的 AI/开发者去落地 */
  const copyText = (text: string): Promise<void> => {
    if (navigator.clipboard?.writeText && window.isSecureContext) return navigator.clipboard.writeText(text);
    return new Promise<void>((resolve, reject) => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy') ? resolve() : reject(new Error('copy failed'));
      } catch (e) {
        reject(e as Error);
      } finally {
        document.body.removeChild(ta);
      }
    });
  };

  const adoptIdea = async (idea: IdeaCard) => {
    await setIdeaStatus(idea, 'adopted', '已采纳');
    const req = [
      `【智坊改进需求 · 来自创意中心】${idea.title}`,
      '',
      '创意内容:',
      idea.content,
      ...(idea.inspiration ? ['', '对智坊的启发:', idea.inspiration] : []),
    ].join('\n');
    try {
      await copyText(req);
      message.success('改进需求已复制到剪贴板,粘贴给负责改智坊的 AI/开发者即可');
    } catch {
      message.info('自动复制没成功,请在详情里手动选中复制');
    }
  };

  const retryBatch = async (b: IdeaBatch) => {
    try {
      const r = await ideaIntelApi.retryBatch(b.id);
      if (r?.ok) { message.success('已重新排队提炼'); loadBatches(batchPage); }
      else message.error(r?.reason || '重试失败');
    } catch {
      message.error('重试失败');
    }
  };

  const batchColumns = [
    { title: '时间', dataIndex: 'startedAt', width: 130, render: (v: string) => fmtTime(v, 'MM-DD HH:mm:ss') },
    { title: '消息数', dataIndex: 'msgCount', width: 80 },
    { title: '触发原因', dataIndex: 'triggerReason', width: 110 },
    { title: '模型', dataIndex: 'modelUsed', width: 160, render: (v: string) => v || '-' },
    {
      title: 'Token',
      key: 'tokens',
      width: 120,
      render: (_: any, r: IdeaBatch) => `${r.promptTokens}/${r.completionTokens}`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (v: string) => <span className={`ic-batch-status ic-batch-${v}`}>{v}</span>,
    },
    {
      title: '说明/操作',
      key: 'op',
      render: (_: any, r: IdeaBatch) => (
        <Space>
          {r.error && (
            <Tooltip title={r.error}>
              <span className="ic-batch-err">{r.error.slice(0, 40)}…</span>
            </Tooltip>
          )}
          {r.status === 'failed' && (
            <Button size="small" icon={<RedoOutlined />} onClick={() => retryBatch(r)}>重试</Button>
          )}
        </Space>
      ),
    },
  ];

  const ideaList = (
    <>
      <div className="ic-filters">
        <Input
          allowClear
          placeholder="搜索标题/内容/灵感/来源…"
          prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
          style={{ width: 240 }}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Select
          allowClear
          placeholder="全部分类"
          style={{ width: 150 }}
          value={category}
          onChange={(v) => { setCategory(v); setPage(1); }}
          options={CATEGORIES.map((c) => ({
            value: c,
            label: `${c}${overview?.categories.find((x) => x.category === c)?.count ? ` (${overview.categories.find((x) => x.category === c)!.count})` : ''}`,
          }))}
        />
        <Select
          allowClear
          placeholder="全部状态"
          style={{ width: 130 }}
          value={status}
          onChange={(v) => { setStatus(v); setPage(1); }}
          options={Object.entries(STATUS_TEXT).map(([v, t]) => ({ value: v, label: t }))}
        />
        <Button icon={<ReloadOutlined />} onClick={() => loadIdeas(page)}>刷新</Button>
      </div>

      <Spin spinning={loading}>
        {ideas.length === 0 && !loading ? (
          <div className="ic-empty">
            <BulbOutlined className="ic-empty-icon" />
            <div className="ic-empty-title">还没有创意卡片</div>
            <div className="ic-empty-sub">{PIPELINE_HINT}</div>
          </div>
        ) : (
          <div className="ic-list">
            {ideas.map((it) => (
              <div
                key={it.id}
                className={`ic-row ic-row-${it.status}`}
                onClick={() => setDetailIdea(it)}
                role="button"
                tabIndex={0}
              >
                <span className={CATEGORY_CLS[it.category] || 'ic-cat'}>{it.category}</span>
                {scoreStars(it.score)}
                <span className="ic-row-title" title={it.title}>{it.title}</span>
                <span className={`ic-status ic-status-${it.status}`}>{STATUS_TEXT[it.status] || it.status}</span>
                <span className="ic-row-time">{fmtTime(it.occurredAt || it.createdAt)}</span>
              </div>
            ))}
          </div>
        )}
      </Spin>

      {total > 0 && (
        <div className="ic-pager">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={setPage}
            showSizeChanger={false}
            showTotal={(t) => `共 ${t} 条创意`}
          />
        </div>
      )}
    </>
  );

  return (
    <div className="ic-page df-dark-page">
      <div className="ic-hero">
        <h1 className="ic-hero-title"><BulbOutlined /> 智坊创意中心</h1>
        <div className="ic-hero-sub">
          从你的聊天圈里挖 AI 新技术 · 新工具 · 新点子,给智坊的进化当灵感——采纳后一键复制改进需求,交给维护智坊的 AI 落地
        </div>
        <div className="ic-hero-stats">
          <div className="ic-hero-stat"><b>{overview?.newCount ?? '-'}</b><span>待看新创意</span></div>
          <div className="ic-hero-stat"><b>{overview?.total ?? '-'}</b><span>累计创意</span></div>
          <div className="ic-hero-stat"><b>{overview?.adopted ?? '-'}</b><span>已采纳</span></div>
          <div className="ic-hero-stat">
            <b>{overview?.lastBatch ? fmtTime(overview.lastBatch.startedAt) : '-'}</b>
            <span>最近提炼</span>
          </div>
        </div>
      </div>

      <Tabs
        defaultActiveKey="ideas"
        items={[
          { key: 'ideas', label: <span><BulbOutlined /> 创意卡片</span>, children: ideaList },
          {
            key: 'batches',
            label: <span><FileSearchOutlined /> 提炼批次</span>,
            children: (
              <>
                <Table
                  rowKey="id"
                  size="small"
                  columns={batchColumns as any}
                  dataSource={batches}
                  pagination={false}
                />
                {batchTotal > 20 && (
                  <div className="ic-pager">
                    <Pagination current={batchPage} pageSize={20} total={batchTotal} onChange={setBatchPage} showSizeChanger={false} />
                  </div>
                )}
              </>
            ),
          },
        ]}
      />

      <TraceModal idea={traceIdea} onClose={() => setTraceIdea(null)} />

      {/* 创意详情弹窗:列表点开看完整内容 */}
      <Modal
        open={!!detailIdea}
        onCancel={() => setDetailIdea(null)}
        footer={null}
        width={720}
        className="ic-detail-modal df-readable-modal"
        title={null}
        destroyOnClose
      >
        {detailIdea && (
          <div className="ic-detail">
            <div className="ic-detail-head">
              <span className={CATEGORY_CLS[detailIdea.category] || 'ic-cat'}>{detailIdea.category}</span>
              {scoreStars(detailIdea.score)}
              <span className={`ic-status ic-status-${detailIdea.status}`}>
                {STATUS_TEXT[detailIdea.status] || detailIdea.status}
              </span>
            </div>
            <h2 className="ic-detail-title">{detailIdea.title}</h2>

            <div className="ic-detail-label">详细内容</div>
            <div className="ic-detail-content">{detailIdea.content}</div>

            {detailIdea.inspiration && (
              <>
                <div className="ic-detail-label"><ThunderboltOutlined /> 对智坊的启发</div>
                <div className="ic-detail-insp">{detailIdea.inspiration}</div>
              </>
            )}

            <div className="ic-detail-src">
              {detailIdea.sourceUrl ? (
                <a href={detailIdea.sourceUrl} target="_blank" rel="noreferrer" className="ic-src-link">
                  🌐 {detailIdea.sourceName || '网络情报'} · 查看原文
                </a>
              ) : detailIdea.source ? (
                <span>
                  {detailIdea.source.isGroup ? '👥' : '👤'} {detailIdea.source.talkerName}
                  {detailIdea.source.sender ? ` · ${detailIdea.source.sender}` : ''}
                </span>
              ) : <span />}
              <span>{fmtTime(detailIdea.occurredAt || detailIdea.createdAt)}</span>
            </div>

            <div className="ic-detail-actions">
              <Tooltip title="这些创意是改进智坊平台本身用的,智坊没法自己改自己。点击后标记「已采纳」,并把整理好的改进需求复制到剪贴板,粘贴给负责维护智坊的 AI/开发者去实现">
                <Button type="primary" icon={<CopyOutlined />} onClick={() => { adoptIdea(detailIdea); setDetailIdea(null); }}>
                  采纳,复制改进需求
                </Button>
              </Tooltip>
              {detailIdea.status === 'new' && (
                <Button icon={<CheckCircleOutlined />} onClick={() => setIdeaStatus(detailIdea, 'read', '已标记已读')}>
                  标记已读
                </Button>
              )}
              {detailIdea.status !== 'ignored' && (
                <Button icon={<StopOutlined />} onClick={() => { setIdeaStatus(detailIdea, 'ignored', '已忽略'); setDetailIdea(null); }}>
                  忽略
                </Button>
              )}
              {!detailIdea.sourceUrl && (
                <Button icon={<EyeOutlined />} onClick={() => { setTraceIdea(detailIdea); setDetailIdea(null); }}>
                  溯源
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
